import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  fetchInteractionById,
  postMatchOverride,
  unlockVaultManual,
} from "../services/matchService";
import Loader from "../child/Loder";

const EditMatchPageNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fromUser: "",
    toUser: "",
    action: "",
    status: "",
    reason: "", // add this
  });

  // Vault Unlock State
  const [vaultData, setVaultData] = useState({
    userId: "",
    matchedUserId: "",
    unlockType: "tier",
    tier: "iceBreaker",
    permanent: false,
    reason: "",
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("match-status");
  const [isUnmatched, setIsUnmatched] = useState(false);
  const [vaultErrors, setVaultErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Fetch interaction details on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetchInteractionById(id);
        if (res?.status === 200) {
          const Matchdata = res?.data?.data;
          setFormData({
            fromUser: Matchdata.fromUser?.email || "N/A",
            toUser: Matchdata.toUser?.email || "N/A",
            action: Matchdata.action || "",
            status: Matchdata.status || "",
            reason: Matchdata.reason || "",
            fromUserId: Matchdata.fromUser?._id || "",
            toUserId: Matchdata.toUser?._id || "",
            unmatchedBy: Matchdata.unmatchedBy || null,
            unmatchedAt: Matchdata.unmatchedAt || null,
          });
          setIsUnmatched(Matchdata.status === "unmatched"); // ✅ flag

          // Initialize vault unlock IDs
          setVaultData((prev) => ({
            ...prev,
            userId: Matchdata.fromUser?._id || "",
            matchedUserId: Matchdata.toUser?._id || "",
          }));
        } else {
          setErrorMessage(res?.message || "Failed to fetch interaction data");
        }
      } catch (err) {
        setErrorMessage(err?.message || "Failed to fetch interaction data");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.reason || !formData.reason.trim()) {
      errors.reason = "Reason is required";
    } else if (formData.reason.trim().length < 5) {
      errors.reason = "Reason should be at least 5 characters";
    } else if (formData.reason.trim().length > 200) {
      errors.reason = "Reason cannot exceed 200 characters";
    }

    // Add other validations if needed

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return; // stop submission if invalid

    // Prevent override if unmatched
    if (isUnmatched) {
      setErrorMessage(
        "This match was unmatched by the user and cannot be reversed."
      );
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const payload = {
        interactionId: id,
        action: formData.action, // "curiouzz" or "super_curiouzz"
        status: formData.status, // "match", "blocked", etc.
        reason: formData.reason || "No reason provided",
      };

      const res = await postMatchOverride(payload);
      if (res?.data?.status === 1) {
        setSuccessMessage("Interaction updated successfully!");
        setTimeout(() => navigate("/match-control"), 1500);
      } else {
        setErrorMessage(res?.message || "Failed to update interaction");
      }
    } catch (err) {
      setErrorMessage(err?.message || "Error while updating interaction");
    } finally {
      setLoading(false);
    }
  };

  const handleVaultChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVaultData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setVaultErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateVaultForm = () => {
    const errors = {};

    if (!vaultData.reason || !vaultData.reason.trim()) {
      errors.reason = "Reason is required";
    } else if (vaultData.reason.trim().length < 5) {
      errors.reason = "Reason should be at least 5 characters";
    } else if (vaultData.reason.trim().length > 200) {
      errors.reason = "Reason cannot exceed 200 characters";
    }

    // optional: add validation for tier selection if needed
    if (vaultData.unlockType === "tier" && !vaultData.tier) {
      errors.tier = "Tier is required";
    }

    setVaultErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit Vault unlock
  const handleVaultSubmit = async (e) => {
    e.preventDefault();
    if (!validateVaultForm()) return;
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const payload = {
        userId: vaultData.userId, // ✅ fromUser
        matchedUserId: vaultData.matchedUserId, // ✅ toUser
        unlockType: vaultData.unlockType,
        tier: vaultData.unlockType === "tier" ? vaultData.tier : undefined,
        // permanent: vaultData.permanent,
        permanent:true,
        reason: vaultData.reason,
         unlockByAdmin: true, 
      };

      const res = await unlockVaultManual(payload);
      if (res?.data?.status === 1) {
        setSuccessMessage("Vault unlocked successfully!");
        setTimeout(() => navigate("/match-control"), 1500);
      } else {
        setErrorMessage(res?.data?.message || "Failed to unlock vault");
      }
    } catch (err) {
      setErrorMessage(err?.message || "Error while unlocking vault");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-4">
        {successMessage && (
          <SuccessAlert
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
        {errorMessage && (
          <DengerAlert
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )}
      </div>
      <div className="col-md-12">
        <div className="card h-100">
          <div className="card-body p-24">
            {/* Tab Navigation */}
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="match-discovery-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "match-status" ? "active" : ""
                  }`}
                  id="tab-match-status"
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("match-status")}
                >
                  Match Status Override
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "vault-unlock" ? "active" : ""
                  }`}
                  id="tab-vault-unlock"
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("vault-unlock")}
                >
                  Vault Unlock
                </button>
              </li>
            </ul>

            <div className="tab-content" id="match-discovery-tab-content">
              <div
                className={`tab-pane fade ${
                  activeTab === "match-status" ? "show active" : ""
                }`}
                id="tab-content-match-status"
                role="tabpanel"
              >
                <form onSubmit={handleSubmit}>
                  {/* From User */}
                  <div className="mb-3">
                    <label className="form-label">From User</label>
                    <input
                      type="text"
                      className="form-control"
                      name="fromUser"
                      value={formData.fromUser}
                      disabled
                    />
                  </div>

                  {/* To User */}
                  <div className="mb-3">
                    <label className="form-label">To User</label>
                    <input
                      type="text"
                      className="form-control"
                      name="toUser"
                      value={formData.toUser}
                      disabled
                    />
                  </div>

                  {/* Action */}
                  <div className="mb-3">
                    <label className="form-label">Action</label>
                    <select
                      className="form-select"
                      name="action"
                      value={formData.action}
                      onChange={handleChange}
                      required
                      // disabled
                      disabled={isUnmatched}
                    >
                      <option value="" disabled>
                        Select Actions
                      </option>
                      <option value="curiouzz">Curiouzz</option>
                      <option value="super_curiouzz">Super Curiouzz</option>
                      <option value="not_curiouzz">Not Curiouzz</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                      disabled={isUnmatched} // ✅ disable editing if unmatched
                    >
                      <option value="" disabled>
                        Select Status
                      </option>
                      <option value="pending">Pending</option>
                      <option value="seen">Seen</option>
                      <option value="replied">Replied</option>
                      <option value="matched">Matched</option>
                      <option value="rejected">Rejected</option>
                      <option value="expired">Expired</option>
                      <option value="blocked">Blocked</option>
                      {formData.status === "unmatched" && (
                        <option value="unmatched">Unmatched</option>
                      )}
                    </select>
                    {formData.status === "unmatched" && (
                      <small className="text-danger">
                        This match was unmatched by the user and cannot be
                        reversed.
                      </small>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Reason</label>
                    <input
                      type="text"
                      // className="form-control"
                      className={`form-control ${
                        formErrors.reason ? "is-invalid" : ""
                      }`}
                      name="reason"
                      value={formData.reason || ""}
                      onChange={handleChange}
                      placeholder="Enter reason"
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                          .trimStart(); // Prevent leading spaces
                      }}
                      disabled={isUnmatched} // ✅ disable editing if unmatched
                    />
                    {formErrors.reason && (
                      <div className="invalid-feedback">
                        {formErrors.reason}
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {/* <Icon icon="mdi:content-save" className="me-1" /> */}
                      {loading ? "Saving..." : "Update Match"}
                    </button>
                  </div>
                </form>
              </div>

              {/* Vault Unlock Tab */}
              <div
                className={`tab-pane fade ${
                  activeTab === "vault-unlock" ? "show active" : ""
                }`}
                id="tab-content-vault-unlock"
                role="tabpanel"
              >
                <form onSubmit={handleVaultSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Unlock Type</label>
                    <select
                      name="unlockType"
                      value={vaultData.unlockType}
                      onChange={handleVaultChange}
                      className="form-select"
                    >
                      <option value="tier">Tier</option>
                      <option value="all">All Tiers</option>
                      <option value="reveal">Reveal Together</option>
                    </select>
                  </div>

                  {vaultData.unlockType === "tier" && (
                    <div className="mb-3">
                      <label className="form-label">Tier</label>
                      <select
                        name="tier"
                        value={vaultData.tier}
                        onChange={handleVaultChange}
                        className="form-select"
                      >
                        <option value="iceBreaker">Ice Breaker</option>
                        <option value="deeperDive">Deeper Dive</option>
                        <option value="wildCard">Wild Card</option>
                        <option value="connectionGoals">
                          Connection Goals
                        </option>
                      </select>
                    </div>
                  )}

                  {vaultData.unlockType === "reveal" && (
                    <div
                      className="mb-3 form-check"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name="permanent"
                        checked={vaultData.permanent}
                        onChange={handleVaultChange}
                      />
                      <label className="form-check-label">
                        Permanent Unlock
                      </label>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Reason</label>
                    <input
                      type="text"
                      className={`form-control ${
                        vaultErrors.reason ? "is-invalid" : ""
                      }`}
                      name="reason"
                      value={vaultData.reason}
                      onChange={handleVaultChange}
                      placeholder="Enter reason"
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                          .trimStart(); // Prevent leading spaces
                      }}
                    />
                    {vaultErrors.reason && (
                      <div className="invalid-feedback">
                        {vaultErrors.reason}
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Processing..." : "Vault Unlock"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditMatchPageNew;
